import { useCallback, useRef } from 'react';
import {
  PRIVACY_POLICY_LABEL,
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_LABEL,
  TERMS_OF_SERVICE_URL,
} from 'common/constants/auth';
import { Box, Button, Checkbox, Stack, Text, TextLink } from 'components/ui';
import { useAuthInitialFocus } from 'pages/Auth/hooks';
import {
  SIGN_IN_TERMS_AGREEMENT_LEAD,
  SIGN_IN_TERMS_AGREEMENT_MIDDLE,
  SIGN_IN_TERMS_SIGN_OUT_LABEL,
  SIGN_IN_TERMS_SUBMIT_LABEL,
} from 'pages/Auth/SignIn/SignIn.constants';
import { getVisibleFieldErrorMessage } from 'pages/Auth/utils';
import type { SignInTermsStepProps } from './SignInTermsStep.types';

const signInTermsLinkColorSx = { color: 'primary.main' };

const SignInTermsStep = ({ form, isSubmitting, onSignOut, onSubmit }: SignInTermsStepProps) => {
  const termsInputRef = useRef<HTMLInputElement>(null);
  const focusTermsCheckbox = useCallback(() => {
    termsInputRef.current?.focus();
  }, []);
  const hasAcceptedTerms = form.watch('acceptTerms');
  const isSubmitDisabled = isSubmitting || !hasAcceptedTerms;
  const helperText = getVisibleFieldErrorMessage({
    error: form.formState.errors.acceptTerms,
    hiddenErrorTypes: ['server'],
  });

  useAuthInitialFocus(focusTermsCheckbox, !isSubmitting);

  return (
    <Stack component="form" noValidate onSubmit={onSubmit} spacing={2.5}>
      <Stack spacing={0.75}>
        <Box display="flex" justifyContent="flex-end" minHeight="24px" width="100%">
          {helperText ? (
            <Text component="span" variant="caption" visualVariant="fieldInlineError">
              {helperText}
            </Text>
          ) : null}
        </Box>
        <Checkbox
          checked={hasAcceptedTerms}
          disabled={isSubmitting}
          error={Boolean(form.formState.errors.acceptTerms)}
          inputRef={termsInputRef}
          label={
            <Box component="span" sx={{ color: 'text.primary' }}>
              {SIGN_IN_TERMS_AGREEMENT_LEAD}{' '}
              <TextLink
                href={TERMS_OF_SERVICE_URL}
                rel="noopener noreferrer"
                sx={signInTermsLinkColorSx}
                target="_blank"
                variant="body2"
                visualVariant="inlineAnchor"
              >
                {TERMS_OF_SERVICE_LABEL}
              </TextLink>{' '}
              {SIGN_IN_TERMS_AGREEMENT_MIDDLE}{' '}
              <TextLink
                href={PRIVACY_POLICY_URL}
                rel="noopener noreferrer"
                sx={signInTermsLinkColorSx}
                target="_blank"
                variant="body2"
                visualVariant="inlineAnchor"
              >
                {PRIVACY_POLICY_LABEL}
              </TextLink>
              .
            </Box>
          }
          onChange={checked => form.setValue('acceptTerms', checked, { shouldDirty: true, shouldValidate: true })}
        />
      </Stack>
      <Stack direction="row" justifyContent="flex-end" spacing={2} width="100%">
        <Button
          disabled={isSubmitting}
          id="auth-terms-sign-out-button"
          onClick={onSignOut}
          type="button"
          variant="text"
          visualVariant="primaryGhost"
        >
          {SIGN_IN_TERMS_SIGN_OUT_LABEL}
        </Button>
        <Button
          disabled={isSubmitDisabled}
          id="auth-terms-next-button"
          loading={isSubmitting}
          loadingBehavior="inline"
          sizeVariant="compact"
          type="submit"
          variant="contained"
          visualVariant="positive"
        >
          {SIGN_IN_TERMS_SUBMIT_LABEL}
        </Button>
      </Stack>
    </Stack>
  );
};

export default SignInTermsStep;
